import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Lock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

function formatError(detail) {
  if (!detail) return "Login failed.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Already signed in? Skip the page.
  useEffect(() => {
    // user can be null (checking), false (anon), or object
  }, [user]);

  if (user && typeof user === "object") {
    return <Navigate to="/admin" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Email and password required.");
      return;
    }
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back.");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail) || "Invalid credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="relative min-h-screen bg-[#050505] text-white overflow-hidden flex items-center justify-center px-6"
    >
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full opacity-30 blur-[120px]"
          style={{ background: "radial-gradient(circle, #29ABE2 0%, transparent 70%)" }}
        />
        <div
          className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full opacity-20 blur-[120px]"
          style={{ background: "radial-gradient(circle, #29ABE2 0%, transparent 70%)" }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md"
      >
        <a
          href="/"
          className="flex items-center justify-center mb-10"
          data-testid="admin-login-home"
        >
          <img
            src="/NaN-Logotype-05-300x169.png"
            alt="NaN Studio"
            className="h-14 w-auto"
          />
        </a>

        <div className="border border-white/[0.08] bg-[#0a0a0a]/80 backdrop-blur-xl p-8 md:p-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-full bg-[#29ABE2]/15 border border-[#29ABE2]/40 flex items-center justify-center">
              <Lock size={14} className="text-[#29ABE2]" />
            </div>
            <div className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/50">
              Restricted
            </div>
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-2">
            Admin access
          </h1>
          <p className="text-white/55 text-sm mb-10">
            Sign in to manage projects and inbox.
          </p>

          <form onSubmit={onSubmit} className="space-y-7" data-testid="admin-login-form">
            <label className="block">
              <div className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">
                Email
              </div>
              <input
                data-testid="admin-login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@nanstudio.al"
                autoComplete="email"
                className="w-full bg-transparent border-b border-white/[0.15] focus:border-[#29ABE2] py-2 text-white placeholder:text-white/25 outline-none text-base"
              />
            </label>

            <label className="block">
              <div className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">
                Password
              </div>
              <input
                data-testid="admin-login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                className="w-full bg-transparent border-b border-white/[0.15] focus:border-[#29ABE2] py-2 text-white placeholder:text-white/25 outline-none text-base"
              />
            </label>

            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="group w-full inline-flex items-center justify-center gap-3 px-7 py-3.5 rounded-full bg-[#29ABE2] text-black font-medium text-sm hover:bg-[#5cc2ec] transition-colors disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
              <ArrowRight
                size={14}
                className="transition-transform group-hover:translate-x-1"
              />
            </button>

            <div className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/35 text-center">
              Sessions expire after 12 hours
            </div>
          </form>
        </div>

        <a
          href="/"
          className="block mt-8 text-center font-mono-accent text-[11px] uppercase tracking-[0.3em] text-white/40 hover:text-white"
        >
          ← Back to site
        </a>
      </motion.div>
    </div>
  );
}
